import { DatacentralHookForm } from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { applyPriceChangeRequest } from '@/modules/Service/services'
import type { PriceChangeRequest } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface ApplyPriceChangeModalProps extends ModalProps {
    priceChangeRequest: PriceChangeRequest
}

interface FormValues {
    effective_from: string
}

const ApplyPriceChangeModal: React.FC<ApplyPriceChangeModalProps> = ({
    open,
    onClose,
    priceChangeRequest,
}) => {
    const { t } = useTranslation('services')
    const { t: tCommon } = useTranslation()

    const formControl = useForm<FormValues>({
        defaultValues: {
            effective_from: dayjs().format('YYYY-MM-DD'),
        },
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_PRICE_CHANGE_REQUESTS],
    })

    const { mutateAsync: applyMutation, isPending } = useMutationWithHandler(
        { mutationFn: applyPriceChangeRequest },
        {
            successMessage: t('price_change_requests.apply_success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_PRICE_CHANGE_REQUESTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmit = (values: FormValues) => {
        applyMutation({
            id: priceChangeRequest.id,
            effective_from: values.effective_from,
        })
    }

    const oldPrice = priceChangeRequest.old_price?.toLocaleString() ?? '—'
    const newPrice = priceChangeRequest.new_price?.toLocaleString() ?? '—'

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('price_change_requests.apply_title')}
        >
            <div className="mb-4 text-sm text-muted-foreground">
                {t('price_change_requests.apply_description', {
                    old_price: oldPrice,
                    new_price: newPrice,
                    service_name: priceChangeRequest.service_name,
                })}
            </div>
            <DatacentralHookForm<FormValues>
                formControl={formControl}
                onSubmit={onSubmit}
                columns={1}
                data={[
                    {
                        formItemProps: {
                            name: 'effective_from',
                            label: t('price_change_requests.effective_from'),
                            type: 'datepicker',
                            required: true,
                        },
                        colSpan: 1,
                    },
                ]}
                actions={[
                    {
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        title: tCommon('cancel'),
                    },
                    {
                        type: 'submit',
                        variant: 'default',
                        title: t('price_change_requests.apply'),
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default ApplyPriceChangeModal
